import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDateLocale, dateDiff, now } from '@/common/utils/date';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Archive, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/auth';
import { canArchivarEscrituras, checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { TareasHeaderBadge } from '@/tramite-tareas';
import type { TareasCountSource } from '@/tramite-tareas';
import { BloqueoBanner } from '@/components/BloqueoBanner';
import { BloqueoTramiteDialog } from '@/components/BloqueoTramiteDialog';
import { useToggleBloqueoTramite } from '@/hooks/useToggleBloqueoTramite';
import { ArchivadoBanner } from '@/components/ArchivadoBanner';
import { ArchivarTramiteDialog } from '@/components/ArchivarTramiteDialog';
import { useArchivarTramite } from '@/hooks/useArchivarTramite';
import { useAlert } from '@/hooks/useAlert';
import type { Tramite } from '@/types';

interface HeaderTramiteProps {
  /** Trámite "vivo" (del fetch de `useDashboard`, no el de `location.state`, que
   * queda desactualizado apenas se marca una tarea). Opcional: sin permiso o sin
   * datos, el badge de tareas simplemente no se muestra. */
  tareasTramite?: TareasCountSource;
  onTareasChanged?: () => void;
  /** Trámite vivo con los campos de bloqueo, para no depender del `tramite` stale
   * de `location.state`. */
  tramiteVivo?: Pick<
    Tramite,
    | 'id'
    | 'bloqueado'
    | 'bloqueado_motivo'
    | 'bloqueado_at'
    | 'bloqueado_por'
    | 'archivado'
    | 'archivado_motivo'
    | 'archivado_at'
    | 'archivado_por'
  >;
  onBloqueoChanged?: () => void;
  onArchivadoChanged?: () => void;
}

const HeaderTramite = ({ tareasTramite, onTareasChanged, tramiteVivo, onBloqueoChanged, onArchivadoChanged }: HeaderTramiteProps) => {
  const { state: { tramite } } = useLocation();
  const { user } = useAuth();
  const { questionAlert } = useAlert();
  const userPermissions = user?.permissions ?? [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canViewTareas =
    checkAllPermissions(userPermissions, [PERMISSIONS.VIEW_TRAMITE_TAREAS.name]) ||
    checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITE_TAREAS.name]);
  const canBloquear = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_BLOQUEO_TRAMITE.name]);
  const canArchivar = canArchivarEscrituras(userPermissions);

  const [isBloqueoDialogOpen, setIsBloqueoDialogOpen] = useState(false);
  const { toggle: toggleBloqueo, isSubmitting: isTogglingBloqueo } = useToggleBloqueoTramite(() => onBloqueoChanged?.());

  const [isArchivarDialogOpen, setIsArchivarDialogOpen] = useState(false);
  const { toggle: toggleArchivado, isSubmitting: isTogglingArchivado } = useArchivarTramite(() => onArchivadoChanged?.());

  const handleDesarchivar = () => {
    if (!tramiteVivo) return;
    questionAlert({
      title: 'Desarchivar escritura',
      text: '¿Esta seguro que desea desarchivar esta escritura?',
      icon: 'question',
    }).then((r) => {
      if (r.isConfirmed) {
        toggleArchivado({ id: tramiteVivo.id, archivado: false }).catch(() => {
          /* el error ya se notifica en el hook */
        });
      }
    });
  };

  const handleDesbloquear = () => {
    if (!tramiteVivo) return;
    questionAlert({
      title: 'Desbloquear escritura',
      text: '¿Esta seguro que desea desbloquear esta escritura?',
      icon: 'question',
    }).then((r) => {
      if (r.isConfirmed) {
        toggleBloqueo({ id: tramiteVivo.id, bloqueado: false }).catch(() => {
          /* el error ya se notifica en el hook */
        });
      }
    });
  };

  return (
    <div className="mb-6">
      <div className="bg-white shadow-md text-xs sm:text-sm flex flex-col sm:flex-row rounded p-4 flex-wrap justify-center items-center">
        <div className="px-2 font-semibold">
          Fecha de inicio: {tramite.fecini ? formatDateLocale(tramite.fecini) : '-'}
        </div>
        {canManageTramites && tramite.fecini &&
          <div className="px-2 font-semibold">
            Días desde el inicio: {dateDiff(now(), tramite.fecini, 'days')}
          </div>
        }
        <div className="min-w-0 break-words px-2 font-semibold">Propiedad: {tramite.propiedad}</div>
        <div className="px-2 font-semibold"># Registro: {tramite.numregistro || '-'}</div>
        {canViewTareas && tareasTramite && (
          <div className="px-2">
            <TareasHeaderBadge tramite={tareasTramite} onChanged={onTareasChanged} />
          </div>
        )}
        {canBloquear && tramiteVivo && !tramiteVivo.bloqueado && (
          <div className="px-2">
            <Badge
              variant="outline"
              className="cursor-pointer px-2 py-1 hover:opacity-80"
              onClick={() => setIsBloqueoDialogOpen(true)}
            >
              <Lock className="h-3.5 w-3.5" /> Bloquear
            </Badge>
          </div>
        )}
        {canArchivar && tramiteVivo && !tramiteVivo.archivado && (
          <div className="px-2">
            <Badge
              variant="outline"
              className="cursor-pointer px-2 py-1 hover:opacity-80"
              onClick={() => setIsArchivarDialogOpen(true)}
            >
              <Archive className="h-3.5 w-3.5" /> Archivar
            </Badge>
          </div>
        )}
        {canManageTramites &&
          <ReactRouterLink
            to="/historial"
            state={{ tramite }}
            className="text-blue-500 text-center hover:underline"
          >
            Ver historial de cambios
          </ReactRouterLink>
        }
      </div>

      {tramiteVivo && (
        <BloqueoBanner
          tramite={tramiteVivo}
          onDesbloquear={canBloquear ? handleDesbloquear : undefined}
          isSubmitting={isTogglingBloqueo}
          className="mt-3"
        />
      )}

      {tramiteVivo && (
        <ArchivadoBanner
          tramite={tramiteVivo}
          onDesarchivar={canArchivar ? handleDesarchivar : undefined}
          isSubmitting={isTogglingArchivado}
          className="mt-3"
        />
      )}

      {tramiteVivo && (
        <ArchivarTramiteDialog
          isOpen={isArchivarDialogOpen}
          onClose={() => setIsArchivarDialogOpen(false)}
          isSubmitting={isTogglingArchivado}
          bloqueadasCount={tramiteVivo.bloqueado ? 1 : 0}
          onConfirm={async (motivo) => {
            try {
              await toggleArchivado({ id: tramiteVivo.id, archivado: true, motivo });
              setIsArchivarDialogOpen(false);
            } catch {
              /* el error ya se notifica en el hook */
            }
          }}
        />
      )}

      {tramiteVivo && (
        <BloqueoTramiteDialog
          tramite={tramiteVivo}
          isOpen={isBloqueoDialogOpen}
          onClose={() => setIsBloqueoDialogOpen(false)}
          onBlocked={() => onBloqueoChanged?.()}
        />
      )}
    </div>
  );
}

export default HeaderTramite;